import { useHistory, useParams } from 'react-router-dom';
import React, { useState, useEffect } from 'react';
import store, { set } from 'store';
import useSWR from 'swr';

import { getSWR, post } from 'utils/api';
import { getChildJournal } from 'child-journals/api';
import { useCurrentMember } from 'members/hooks/index';
import { calculatePricing } from 'app/payment/DashboardPrices';
import useLocation from 'app/hooks/location';
import { postMemberEvent } from 'members/api';
import { getAllPackages, getPrefixedEventName } from 'app/form/helper';
import ReadyToPurchase from 'gift-certificate/ReadyToPurchase';
import Wrapper from 'gift-certificate/Wrapper';
import PaymentPage from 'app/payment/PaymentPage';
import PaymentSuccess from 'app/payment/PaymentSuccess';
import { filterUTMParams } from 'members/helper';
import AddEmail from 'gift-certificate/AddEmail';
import { useDispatch } from 'react-redux';
import SignUp from 'babys-first-year/SignUp';
import FAQSection from 'gift-certificate/FAQSection';
import SuccessSection from './SuccessSection';

const WatchMeGrowOnBoarding = () => {
    const [currentChildJournalId, setCurrentChildJournalId] = useState(store.get('child_journal_id') || null);
    const [childJournalDetails, setChildJournalDetails] = useState(null);
    const [step, setStep] = useState(null);
    const [redeemCode, setRedeemCode] = useState(null);
    const dispatch = useDispatch();
    const member = useCurrentMember();
    const { push, goBack } = useHistory();
    const { pathname, search } = useLocation();
    const params = useParams();
    const [orderSummary, setOrderSummary] = useState(null);
    const [generatingInvoice, setGeneratingInvoice] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [isSigningUp, setSigningUp] = useState(false);

    const [email, setEmail] = useState(member?.email);
    const [quantity, setRequiresQuantity] = useState(1);

    useEffect(() => {
        if (email !== member?.email)
            setEmail(member?.email)
    }, [member?.email])

    function getUrl() {
        let param = `?group=watch_me_grow`;
        return `/v3/child_journals/product_packages${param}`;
    }

    const { data: mainData } = useSWR(getUrl(), getSWR);

    const data = mainData?.data

    const calculatedPricing = calculatePricing(data, selectedPlan) || {};
    calculatedPricing.allPackages = getAllPackages(data)

    useEffect(() => {
        if (!calculatedPricing.allPackages?.paidPlans || selectedPlan) return;

        setSelectedPlan(calculatedPricing.allPackages?.paidPlans.find(({ id, relationships }) => !!(relationships?.['product-pricing-plan']?.data)))
    }, [calculatedPricing.allPackages?.paidPlans.length])

    const isFromRedeemCertificate = pathname.includes('/redeem-gift-certificate');

    async function generateInvoice(postalCode) {
        setGeneratingInvoice(true)
        setOrderSummary(null)
        const res = await post(`/v3/product_packages/${calculatedPricing?.packageId || store.get('selectedPackageId')}/invoices` +
            (quantity !== undefined ? `?quantity=${quantity}` : '') +
            (postalCode ? `&postal_code=${postalCode}` : '')
        );
        if (res.status === 200) {
            const lineItems = res?.data?.initial?.data?.relationships?.['line-items']?.data || [];
            const lineItemsDetails = lineItems.map(lineItem => ({
                ...res?.data?.initial?.included.find(({ id, type }) => id === lineItem?.id && type.includes(lineItem?.type))?.attributes,
                id: lineItem?.id,
            }));
            setOrderSummary({
                ...res?.data?.initial?.data?.attributes,
                lineItems: lineItemsDetails,
            });
            setGeneratingInvoice(false)
            return { ...res?.data?.initial?.data?.attributes, ...res?.data?.initial?.included.find(({ id, type }) => id === lineItems?.id && type.includes(lineItems?.type))?.attributes, id: res?.data?.initial?.data?.id }
        }
        else {
            setGeneratingInvoice(false)
            return false
        }
    }

    const getChildJournalDetails = async (storyId) => {
        const { data } = await getChildJournal(storyId);
        setChildJournalDetails(data?.entities?.['child-journals'][storyId]);
    };

    const fetchFirstChildJournal = async () => {
        const { data, status } = await getSWR('/v3/child_journals');
        if (status === 200 && data?.data?.length > 0) {
            const childJournals = data?.data;
            const firstJournal = childJournals?.filter(({ attributes }) =>
                attributes?.['subject-name'] !== "Tap for Intro" &&
                attributes?.['subject-name'] !== 'Sample Child Journal'
            ).sort((a, b) => new Date(a.attributes.createdAt) - new Date(b.attributes.createdAt))[0];

            if (firstJournal) {
                setCurrentChildJournalId(firstJournal?.attributes?.id)
                getChildJournalDetails(firstJournal?.attributes?.id);

                if (!firstJournal?.attributes?.paid)
                    push(`?step=${2}${filterUTMParams(window?.location?.search)}`)
            }
        } else {

        }
    };

    const handleStepChange = (newStep) => {
        push(`?step=${newStep}${filterUTMParams(window?.location?.search)}`)
    };

    async function firePostMemberEvent(eventName, journalId = undefined) {
        if (currentChildJournalId || journalId) {
            postMemberEvent(getPrefixedEventName(eventName), {
                'subject-type': 'ChildJournal',
                'subject-id': journalId || currentChildJournalId,
            })
        } else
            postMemberEvent(getPrefixedEventName(eventName))
    }

    useEffect(() => {
        if (currentChildJournalId) {
            getChildJournalDetails(currentChildJournalId);
        }
    }, [currentChildJournalId]);

    useEffect(() => {
        if (member?.['has-username-password-identifier'] && !isSigningUp)
            setTimeout(fetchFirstChildJournal, 1000);
    }, [member?.['has-username-password-identifier'], isSigningUp]);

    useEffect(() => {
        const queryParams = new URLSearchParams(search);
        const stepFromUrl = queryParams.get('step');
        if (!stepFromUrl) {
            firePostMemberEvent('watch_me_grow_plan_full_feature');
            firePostMemberEvent('where_should_we_send_receipt');
        }

        if (stepFromUrl) {
            setStep(parseInt(stepFromUrl, 10));
        }
        else {
            push(`?step=1${filterUTMParams(window?.location?.search)}`);
        }

    }, [params, pathname]);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [step]);

    useEffect(() => {
        if (step === 2 && (calculatedPricing?.packageId || store.get('selectedPackageId'))) {
            generateInvoice()
        }
    }, [redeemCode, step, calculatedPricing?.packageId, quantity]);

    function skiptoStep(jumpToStep, eventName, journalId) {
        if (typeof jumpToStep === 'number') {
            push(`?step=${jumpToStep}${filterUTMParams(window?.location?.search)}`)
            firePostMemberEvent(eventName || 'save_20_today', journalId)
        }
        else {
            if (eventName)
                firePostMemberEvent(eventName, journalId)

            push(`?step=${step + 1}${filterUTMParams(window?.location?.search)}`)
        }
    }

    const renderStep = () => {
        switch (step) {
            case 1:
                return (
                    <AddEmail
                        redeemCode={redeemCode}
                        setRedeemCode={setRedeemCode}
                        hideSkip
                        firePostMemberEvent={firePostMemberEvent}
                        quantity={quantity}
                        member={member}
                        email={email}
                        setEmail={setEmail}
                        calculatedPricing={calculatedPricing}
                        setRequiresQuantity={setRequiresQuantity}
                        onStepUp={(jumpToStep) => {
                            skiptoStep(undefined, 'review_and_complete_your_order')
                            // window.location.href = `/watch-me-grow/get-started?step=2${filterUTMParams(window?.location?.search)}`
                        }}
                        // onStepDown={() => goBack()}
                        generateInvoice={generateInvoice}
                        isForMyBaby
                        isFromRedeemCertificate={isFromRedeemCertificate}
                        orderSummary={orderSummary}
                    />
                );
            case 2:
                return (
                    <Wrapper
                        hideSkip
                        percentComplete={100}
                        showFooter={false}
                    >
                        <PaymentPage
                            generatingInvoice={generatingInvoice}
                            isForMyBaby
                            backText="Back"
                            nextButtonText={orderSummary?.['grand-total'] === 0 ? "Create Your Account" : "Purchase"}
                            onBack={() => goBack()}
                            onStepUp={async (jumpToStep) => {
                                const isFree = orderSummary?.['grand-total'] === 0
                                skiptoStep(jumpToStep, isFree ? 'create_account' : 'thank_you_for_your_purchase')
                                if (orderSummary?.['grand-total'] === 0) {
                                    await post(`/v3/my/subscriptions/${selectedPlan?.relationships?.['product-pricing-plan']?.data?.id}?force=true`)
                                }
                            }}
                            quantity={quantity}
                            currentChildJournalId={currentChildJournalId}
                            calculatedPricing={calculatedPricing}
                            selectedPlan={selectedPlan}
                            orderSummary={orderSummary}
                            generateInvoice={generateInvoice}
                            firePostMemberEvent={firePostMemberEvent}
                        />
                    </Wrapper>
                );
            case 3:
                return <PaymentSuccess
                    showCta={false}
                    isForMyBaby
                    nextButtonText="Create an Account"
                    quantity={quantity}
                    orderSummary={orderSummary}
                    calculatedPricing={calculatedPricing}
                    currentChildJournalId={currentChildJournalId}
                    attributes={childJournalDetails}
                    onStepUp={() => skiptoStep(undefined, 'create_account')}
                />
            case 4:
                return (
                    <SignUp
                        isForWMG
                        hideSkip
                        firePostMemberEvent={firePostMemberEvent}
                        isLogin={false}
                        redeemCode={redeemCode}
                        percentComplete={25}
                        step={step}
                        setSigningUp={setSigningUp}
                        isSigningUp={isSigningUp}
                        onStepUp={(id) => {
                            if (id)
                                setCurrentChildJournalId(id)
                            skiptoStep(undefined, 'congratulations', id)
                        }}
                        onStepDown={() => {
                            goBack()
                            // skiptoStep(4, 'select_your_plan')
                        }}
                        attributes={childJournalDetails}
                        journalId={currentChildJournalId}
                        setCurrentChildJournalId={setCurrentChildJournalId}
                    />
                );
            case 5:
                return (
                    <SuccessSection
                        firePostMemberEvent={firePostMemberEvent}
                        onStepUp={() => {
                            push(`/child-journals/${currentChildJournalId}/watch-me-grow`);
                        }}
                    />
                );
            default:
                return null;
        }
    };

    return <>
        {renderStep()}
        {/* <Box display={step === 4 ? 'block' : 'none'}>
            <FAQSection
                isForMyBaby
                hideSkip={isFromRedeemCertificate}
                firePostMemberEvent={firePostMemberEvent}
                onStepUp={(jumpToStep) => {
                    if (isFromRedeemCertificate) {
                        if (member?.['has-username-password-identifier'])
                            skiptoStep(10, 'about_your_baby')
                        else
                            skiptoStep(9, 'create_your_account')
                    }
                    else
                        skiptoStep(jumpToStep, 'purchase_details')
                }}
                onStepDown={() => handleStepChange(step - 1)}
            />
        </Box> */}
    </>
};

export default WatchMeGrowOnBoarding;
